import {
  type GameDevStage,
  GameDevStageSchema,
  type SemanticTier,
  type StageColour,
} from '@playerbase/types';

import { supabase } from '../lib/supabase.js';
import { mapGameDevStageRow } from '../shell/shell-data.js';

type RawRow = Parameters<typeof mapGameDevStageRow>[0];

export type CreateDevStageInput = {
  gameId: string;
  label: string;
  colour: StageColour;
  semanticTier: SemanticTier;
  // Caller computes orderIndex (typically max + 1) so this module stays
  // pure and the page is in charge of ordering semantics.
  orderIndex: number;
};

export type UpdateDevStagePatch = {
  label?: string;
  colour?: StageColour;
  semanticTier?: SemanticTier;
};

function parseOne(raw: unknown): GameDevStage | null {
  const parsed = GameDevStageSchema.safeParse(mapGameDevStageRow(raw as RawRow));
  if (!parsed.success) {
    console.warn('[dev-stages] Could not parse row.', parsed.error.issues);
    return null;
  }
  return parsed.data;
}

export async function createDevStage(input: CreateDevStageInput): Promise<GameDevStage | null> {
  const { data, error } = await supabase
    .from('game_dev_stages')
    .insert({
      game_id: input.gameId,
      label: input.label,
      colour: input.colour,
      semantic_tier: input.semanticTier,
      order_index: input.orderIndex,
    })
    .select('*')
    .single();

  if (error) {
    console.warn('[dev-stages] Failed to create:', error.message);
    return null;
  }
  return parseOne(data);
}

export async function updateDevStage(
  id: string,
  patch: UpdateDevStagePatch,
): Promise<GameDevStage | null> {
  const update: Record<string, unknown> = {};
  if (patch.label !== undefined) update.label = patch.label;
  if (patch.colour !== undefined) update.colour = patch.colour;
  if (patch.semanticTier !== undefined) update.semantic_tier = patch.semanticTier;
  if (Object.keys(update).length === 0) return null;

  const { data, error } = await supabase
    .from('game_dev_stages')
    .update(update)
    .eq('id', id)
    .select('*')
    .single();

  if (error) {
    console.warn('[dev-stages] Failed to update:', error.message);
    return null;
  }
  return parseOne(data);
}

// Atomic reorder. Goes through a security-definer RPC because the
// per-row order_index has a deferred unique constraint that only honours
// deferral within a single transaction. Issuing parallel UPDATE calls
// from the client races on the constraint mid-shuffle.
export async function reorderDevStages(
  gameId: string,
  orderedIds: string[],
): Promise<boolean> {
  if (orderedIds.length === 0) return true;
  const { error } = await supabase.rpc('reorder_dev_stages', {
    p_game_id: gameId,
    p_ordered_ids: orderedIds,
  });
  if (error !== null) {
    console.warn('[dev-stages] Failed to reorder:', error.message);
    return false;
  }
  return true;
}

export async function deleteUnusedDevStage(id: string): Promise<boolean> {
  const { error } = await supabase.from('game_dev_stages').delete().eq('id', id);
  if (error) {
    console.warn('[dev-stages] Failed to delete:', error.message);
    return false;
  }
  return true;
}

// Atomic remove + reassign. RPC writes one stage_log entry per affected
// build via the build_markers stage-change trigger.
export async function removeDevStageWithMigration(
  stageId: string,
  targetStageId: string,
): Promise<boolean> {
  const { error } = await supabase.rpc('remove_dev_stage_with_migration', {
    p_stage_id: stageId,
    p_target_stage_id: targetStageId,
  });
  if (error) {
    console.warn('[dev-stages] Failed to remove with migration:', error.message);
    return false;
  }
  return true;
}

// Counts how many active build markers reference the given stage, so the
// remove dialog can decide whether to ask for a migration target.
export async function countBuildMarkersForStage(stageId: string): Promise<number> {
  const { count, error } = await supabase
    .from('build_markers')
    .select('id', { count: 'exact', head: true })
    .eq('stage_id', stageId);

  if (error) {
    console.warn('[dev-stages] Failed to count usages:', error.message);
    return 0;
  }
  return count ?? 0;
}
